import React, { useState } from 'react';
import { useTracking } from 'react-tracking';
import {
  Button, IconType, notification, NotificationConfig, NotificationType, Tooltip,
} from '@routific/hive';
import {
  Button as V5Button,
  PublishIcon,
  Tooltip as V5Tooltip,
} from '@routific/hive/v5';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import usePermissions, { Features } from '../../../../permissions/usePermissions';
import ComponentNames from '../../../../permissions/constants/componentNames';
import PublishModal from '../../../publish/PublishModal';
import { useStore } from '../../../../store/mst';
import AnalyticsEvents from '../../../../analytics/analyticsEvents';
import ActionButton, { CommonActionTypes } from '../../../shared/ActionButton';
import { logglyLogger } from '../../../../services/logglyLogger';
import { ActionButtonProps } from '../types';
import { getDispatchTagViolationProperties } from '../../../../analytics/eventTrackers/dispatch';

export enum PublishButtonType {
  ACTION, DETAIL, ALL,
}

interface PublishActionProps extends ActionButtonProps {
  buttonType?: PublishButtonType;
}

const PublishButton: React.FC<PublishActionProps> = ({
  selectedRouteUuids, type, buttonProps, buttonType, v5, v5Hierarchy,
}) => {
  const { t } = useTranslation('components');
  const { trackEvent } = useTracking();
  const store = useStore();
  const {
    publishableRoutes, ui, model, areAllRoutesPublished,
  } = store;
  const [showPublishSummaryModal, setShowPublishSummaryModal] = useState<boolean>(false);
  const publishingRoutes = publishableRoutes?.filter((r) => selectedRouteUuids.find((uuid) => uuid === r.uuid));
  const anyRoutesAreReversing = store.sortedRoutes.some((r) => r.isReversing);
  const publishableCount = publishingRoutes?.length || 0;
  const allPublished = areAllRoutesPublished && publishableRoutes.length === 0;
  const enablePublishButton = publishableCount > 0;

  const {
    canDispatchRoutes,
  } = usePermissions(Features.Routes, ComponentNames.PublishButton, { isOptimizing: ui.isOptimizing });

  const disabled = !enablePublishButton || !canDispatchRoutes || anyRoutesAreReversing;
  // The footer's lead action stands in for "Schedule all" outside the Unscheduled
  // view (e.g. after scheduling from the Orders page). While routes are being
  // created from templates there are none on screen to show progress, so it
  // carries the in-progress state; other optimizations show it on their routes.
  const isScheduling = buttonType === PublishButtonType.ALL
    && !!ui.isOptimizing
    && store.planningActions.isScaffoldingRoutes;
  const loading = ui.isPublishing || isScheduling;

  const closePublishModal = () => {
    setShowPublishSummaryModal(false);
  };

  const publishFailureNotification = (): NotificationConfig => ({
    id: 'publish-routes-failure',
    type: NotificationType.ERROR,
    message: t('publish.button.error.text'),
    dataCy: 'publish-failure-notification',
  });

  const handlePublish = async () => {
    try {
      await model.publishSelectedRoutes(publishingRoutes.map((r) => r.uuid as string));
      ui.setIsPublishing(true);
      trackEvent({
        event: AnalyticsEvents.DISPATCH_ROUTE,
        properties: {
          displaySummary: showPublishSummaryModal,
          ...getDispatchTagViolationProperties(publishingRoutes),
        },
      });
      if (ui.selectedRoutesBulk.size > 0) {
        ui.clearSelectedRoutesBulk();
      }
    } catch (err) {
      logglyLogger.log({ error: err?.response?.errors?.[0]?.message });
      notification(publishFailureNotification());
    }

    closePublishModal();
  };

  const openPublishSummaryModal = () => {
    setShowPublishSummaryModal(true);
  };

  const getButtonText = () => {
    if (buttonType === PublishButtonType.DETAIL) {
      return t('publish.button.noCount.text');
    }
    if (isScheduling) {
      return t('unscheduledVisits.footer.button.schedulingStops');
    }
    if (allPublished) {
      return t('publish.button.published.text');
    }
    return ui.selectedRoutesBulk?.size > 0 ? t('publish.button.publishSelected.text') : t('publish.button.publishAll.text');
  };

  const getTooltipText = (): string | undefined => {
    if (ui.isOptimizing) {
      return t('publish.tooltip.scheduling');
    }
    if (!enablePublishButton) {
      return t('publish.tooltip.noChangesToPublish');
    }
    return buttonType === PublishButtonType.ACTION ? t('publish.tooltip.button') : undefined;
  };

  // The lead action on the page footer's v5 band. The tooltip only wraps it when
  // there's something to say — a v5 Tooltip renders a trigger wrapper of its own,
  // which would otherwise sit in the band doing nothing.
  const renderV5Button = () => {
    const button = (
      <V5Button
        hierarchy={v5Hierarchy ?? 'primary'}
        size="md"
        loading={loading}
        disabled={disabled}
        onClick={openPublishSummaryModal}
        data-cy="publish-btn"
        data-testid="publish-btn"
      >
        {getButtonText()}
      </V5Button>
    );
    const tooltipText = getTooltipText();

    return tooltipText ? <V5Tooltip title={tooltipText}>{button}</V5Tooltip> : button;
  };

  const renderLegacyButton = () => (
    <Tooltip placement="top" title={getTooltipText()} disabled={disabled}>
      <Button
        loading={loading}
        disabled={disabled}
        type="primary"
        onClick={openPublishSummaryModal}
        data-cy="publish-btn"
      >
        {getButtonText()}
      </Button>
    </Tooltip>
  );

  const renderButton = () => {
    if (buttonType === PublishButtonType.ACTION) {
      return (
        <ActionButton
          v5={v5}
          v5Hierarchy={v5Hierarchy}
          v5Icon={<PublishIcon size="md" />}
          name={CommonActionTypes.PUBLISH}
          tooltipProps={{
            title: getTooltipText(),
          }}
          type={type}
          disabled={disabled}
          menuItemText={t('publish.button.noCount.text')}
          menuItemProps={{
            onClick: openPublishSummaryModal,
          }}
          buttonProps={{
            icon: IconType.Publish,
            style: { pointerEvents: 'auto' },
            onClick: openPublishSummaryModal,
            ...buttonProps,
          }}
        />
      );
    }

    return v5 ? renderV5Button() : renderLegacyButton();
  };

  return (
    <>
      <PublishModal
        visible={showPublishSummaryModal}
        selectedRouteUuids={selectedRouteUuids}
        onPublish={handlePublish}
        onClose={closePublishModal}
      />
      {renderButton()}
    </>
  );
};

export default observer(PublishButton);
